import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { FormEvent } from 'react';
import styles from './shared/CommonModal.module.css';
import '../../styles/generation-common.css';
import { useUploadTemporaryMaterial, useUploadCloudMaterial, useUploadDirectMaterial } from '../../features/materials/hooks';
import { addMaterialProductLinks, fetchDirectUploadMaxBytes } from '../../features/materials/api';
import type { ProductLinkInput } from '../../features/materials/api';
import { useToast } from '../../hooks/useToast';
import { ProductSelectorModal } from './shared/ProductSelectorModal';
import { ProductLinksDisplay } from './shared/MaterialPreviewView';
import { TagSelector } from '../tags';
import type { Tag } from '../../features/tags/types';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';
// 選択中素材のみを購読する (Context 4分割後の個別フック)。
import { useGenerationAssets } from '../generation/state/assetsContext';
import { brandConfig } from '../../config/brands';
import type { AudioReferenceUsage } from '../generation/state/types';
import {
  isDirectUploadTarget,
  isSvgFile,
  isUploadableMaterialFile,
  SVG_LAYER_LOADING_LABEL,
  svgUploadPrecheckError,
  withSvgAccept,
} from '../../features/materials/svgLayerUpload';
import { useSvgLayerUploadGate } from '../../features/materials/useSvgLayerUploadAllowed';
import { layerStackFromMaterial } from '../../features/motion/layerStack';

type SelectedProduct = ProductLinkInput & { title?: string };

// 参照音声の既定用途 (docs/features/reference-audio-roles-plan.md §2.5/§2.6)。
// アップロード時の申告は任意 (未設定=NULL も許容。計画書の記述どおり必須にしない)。
const DEFAULT_AUDIO_ROLE_OPTIONS: Array<{ value: AudioReferenceUsage; label: string }> = [
  { value: 'voice', label: '声' },
  { value: 'bgm', label: 'BGM' },
  { value: 'sound_effect', label: '効果音' },
  { value: 'sound_logo', label: 'サウンドロゴ' },
];

type Props = {
  isOpen: boolean;
  onClose: () => void;
  defaultDestination?: 'temporary' | 'cloud';
  /** 開いた時点で選択済みにするファイル。指定時はファイル選択画面を挟まず
   *  そのままプレビュー/タグ入力状態で開く (画像アップロード → 即ピッカー用)。 */
  initialFile?: File | null;
};

export function UploadMaterialModal({ isOpen, onClose, defaultDestination = 'cloud', initialFile = null }: Props) {
  useLockBodyScroll(isOpen);
  const [destination, setDestination] = useState<'temporary' | 'cloud'>(defaultDestination);
  const [isDragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [notes, setNotes] = useState<string>('');
  // 音声ファイル選択時のみ意味を持つ (§2.5/§2.6)。未選択 = '' は default_audio_role: NULL を送る。
  const [defaultAudioRole, setDefaultAudioRole] = useState<AudioReferenceUsage | ''>('');
  const [showProductSelector, setShowProductSelector] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTempMutation = useUploadTemporaryMaterial();
  const uploadCloudMutation = useUploadCloudMaterial();
  // 動画/音声はブラウザ → GCS 直送 (2GB まで)。画像は従来の multipart 経路。
  const uploadDirectMutation = useUploadDirectMaterial();
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  // uploading = GCS へ PUT 中 (キャンセル可) / registering = 素材登録中 (キャンセル不可:
  // サーバー側の complete 処理は中断できないため)
  const [uploadPhase, setUploadPhase] = useState<'uploading' | 'registering' | null>(null);
  // 直接アップロードのキャンセル用 (abort → キャンセル API 呼び出しは api.ts 側で行う)
  const uploadAbortRef = useRef<AbortController | null>(null);
  const toast = useToast();
  const { addAsset } = useGenerationAssets();

  // モーダルが開いたときにデフォルト選択をリセット。
  // initialFile 指定時はファイル選択済み状態で開く (プレビュー/タグ入力から)。
  useEffect(() => {
    if (isOpen) {
      setDestination(defaultDestination);
      setSelectedProducts([]);
      setTags([]);
      setNotes('');
      setDefaultAudioRole('');
      setDimensions(null);
      if (initialFile) {
        setSelectedFile(initialFile);
        setPreviewUrl(URL.createObjectURL(initialFile));
      } else {
        setSelectedFile(null);
        setPreviewUrl(null);
      }
    }
  }, [isOpen, defaultDestination, initialFile]);

  // プレビューURLをクリーンアップ
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // 画像/動画の解像度を取得
  useEffect(() => {
    if (!previewUrl || !selectedFile) return;

    if (selectedFile.type.startsWith('image/')) {
      const img = new Image();
      img.onload = () => {
        setDimensions({ width: img.width, height: img.height });
      };
      img.src = previewUrl;
    } else if (selectedFile.type.startsWith('video/')) {
      const video = document.createElement('video');
      video.onloadedmetadata = () => {
        setDimensions({ width: video.videoWidth, height: video.videoHeight });
      };
      video.src = previewUrl;
    }
  }, [previewUrl, selectedFile]);

  const isBusy = uploadTempMutation.isPending || uploadCloudMutation.isPending || uploadDirectMutation.isPending;

  // SVG (レイヤー素材) を受けるのはモーショングラフィックスのタブが見える利用者だけ (useSvgLayerUploadGate)。
  // availability は SVG が選ばれたときに確認する
  const svgGate = useSvgLayerUploadGate();

  const handleFileSelect = async (file: File) => {
    // 受けない SVG は「対応していない形式」・16 MB 超は選んだ時点で知らせる
    const allowSvg = await svgGate.resolveAllowSvg([file]);
    const svgPrecheck = svgUploadPrecheckError(file, { allowSvg });
    if (svgPrecheck) {
      toast.error(svgPrecheck);
      return;
    }
    setSelectedFile(file);
    // プレビューURLを生成
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    // ファイルを変更したら既定用途の選択もリセット (前のファイルの選択を引き継がない)
    setDefaultAudioRole('');
  };

  const handleFileChange = (event: FormEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    void handleFileSelect(file);
    input.value = '';
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    const files = Array.from(event.dataTransfer.files || []);
    // SVG (レイヤー素材) も候補にする。受けない利用者は handleFileSelect が「対応していない形式」で弾く
    const firstFile = files.find((file) => isUploadableMaterialFile(file));

    if (firstFile) {
      void handleFileSelect(firstFile);
    }
  };

  // 動画/音声は直接アップロード経路 (16MB 以下でも経路を 1 本化してテスト面を減らす)。
  // SVG は解析経路 (multipart) 固定で直送に乗せない (isDirectUploadTarget・svgLayerUpload.ts)

  const handleUpload = async () => {
    if (!selectedFile) return;

    // SVG は公開範囲と 16 MB を送信前にも確認する (initialFile 経由で handleFileSelect を通らない場合がある)
    const allowSvg = await svgGate.resolveAllowSvg([selectedFile]);
    const svgPrecheck = svgUploadPrecheckError(selectedFile, { allowSvg });
    if (svgPrecheck) {
      toast.error(svgPrecheck);
      return;
    }

    // 直接アップロード対象の事前サイズチェック (発行 API を呼ぶ前に弾く。
    // 上限はバックエンド設定 API から取得して同期)
    if (isDirectUploadTarget(selectedFile)) {
      const maxBytes = await fetchDirectUploadMaxBytes();
      if (selectedFile.size > maxBytes) {
        toast.error(`アップロードできるファイルサイズは最大 ${Math.floor(maxBytes / 1024 / 1024 / 1024)}GB です`);
        return;
      }
    }

    try {
      const uploadPayload: any = { file: selectedFile };

      // タグIDの配列を送信（DBのtags列はUUID配列型）
      if (tags.length > 0) uploadPayload.tags = tags.map(tag => tag.id);
      if (notes) uploadPayload.notes = notes;
      // 音声ファイルのみ意味を持つ (§2.5/§2.6)。未選択のままなら NULL のまま送らない
      if (selectedFile.type.startsWith('audio/') && defaultAudioRole) {
        uploadPayload.default_audio_role = defaultAudioRole;
      }

      let result;
      if (isDirectUploadTarget(selectedFile)) {
        setUploadProgress(0);
        setUploadPhase('uploading');
        const controller = new AbortController();
        uploadAbortRef.current = controller;
        try {
          result = await uploadDirectMutation.mutateAsync({
            ...uploadPayload,
            destination,
            onProgress: (fraction: number) => setUploadProgress(fraction),
            onPhase: (phase: 'uploading' | 'registering') => setUploadPhase(phase),
            signal: controller.signal,
          });
        } finally {
          uploadAbortRef.current = null;
          setUploadProgress(null);
          setUploadPhase(null);
        }
      } else if (destination === 'temporary') {
        result = await uploadTempMutation.mutateAsync(uploadPayload);
      } else {
        result = await uploadCloudMutation.mutateAsync(uploadPayload);
      }

      // アップロード後に商品リンクを追加
      if (selectedProducts.length > 0 && result?.id) {
        const folderType = destination === 'temporary' ? 'temporary' as const : 'cloud' as const;
        const links: ProductLinkInput[] = selectedProducts.map(({ product_type, product_id }) => ({ product_type, product_id }));
        await addMaterialProductLinks(folderType, result.id, links).catch(console.error);
      }

      // 画像の場合のみ選択済み素材に追加（動画・音声はここでは追加しない。
      // 音声は用途宣言 (§2.4/§2.5) を経ずに reference_audio として addAsset へ
      // 渡すことになるため、既定用途申告 UI 実装 (フェーズ3b) までは対象外にする）
      if (result && !selectedFile.type.startsWith('video/') && !selectedFile.type.startsWith('audio/')) {
        const thumbnailUrl = result.thumbnail_url || result.thumbnailUrl || '';
        // SVG は本体が合成 PNG のレイヤー素材になる (content_type は応答の値・layer_stack を写す)
        const layerStack = layerStackFromMaterial(result);
        if (thumbnailUrl) {
          addAsset({
            id: result.id,
            thumbnailUrl,
            type: destination,
            sourceUrl: result.source_url || result.sourceUrl,
            storageBucket: result.storage_bucket,
            storagePath: result.storage_path,
            contentType: result.content_type || selectedFile.type,
            asin: result.asin,
            width: result.width,
            height: result.height,
            ...(layerStack ? { layerStack } : {}),
          });
        }
      }

      // 成功したらモーダルを閉じる
      handleClose();
    } catch (error) {
      // エラーはhookのonErrorで処理される
    }
  };

  const handleClose = () => {
    if (uploadPhase === 'registering') {
      // 登録処理はサーバー側で継続するためキャンセル不可。閉じるだけは許可する
      if (!window.confirm('素材を登録しています。画面を閉じても登録は継続されます。閉じますか？')) {
        return;
      }
    } else if (uploadPhase === 'uploading' && uploadAbortRef.current) {
      // 直接アップロード中に閉じる = アップロードのキャンセル (確認を挟む)
      if (!window.confirm('アップロードをキャンセルして閉じますか？')) {
        return;
      }
      uploadAbortRef.current.abort();
    }
    setSelectedFile(null);
    setSelectedProducts([]);
    setTags([]);
    setNotes('');
    setDefaultAudioRole('');
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    onClose();
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  // ProductSelectorModalに渡す初期選択を安定化（参照が変わるとuseEffectが再実行される）
  const stableSelectedLinks = useMemo(
    () => selectedProducts.map(({ product_type, product_id }) => ({ product_type, product_id })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedProducts.map(p => `${p.product_type}:${p.product_id}`).join(',')],
  );

  if (!isOpen) return null;

  const isVideo = selectedFile?.type.startsWith('video/');
  const isAudio = selectedFile?.type.startsWith('audio/');

  const modalContent = (
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div className={styles.modal}>
        <button type="button" className={styles.closeButton} onClick={handleClose}>
          ✕
        </button>

        <div className={styles.content}>
          {/* 左側：ドラッグ＆ドロップ領域 */}
          <div
            className={`${styles.imagePane} ${styles.dropZone} ${isDragOver ? styles.dragOver : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => !selectedFile && fileInputRef.current?.click()}
          >
            {selectedFile && previewUrl ? (
              <>
                {isVideo ? (
                  <video src={previewUrl} controls className={styles.videoPreview} />
                ) : isAudio ? (
                  <div className={styles.audioPreview}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 18V5l12-2v13" />
                      <circle cx="6" cy="18" r="3" />
                      <circle cx="18" cy="16" r="3" />
                    </svg>
                    <audio src={previewUrl} controls style={{ width: '80%', maxWidth: '360px' }} />
                  </div>
                ) : (
                  <img src={previewUrl} alt="Preview" />
                )}
                <button
                  type="button"
                  className={styles.changeFileButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  ファイルを変更
                </button>
              </>
            ) : (
              <div className={styles.dropPrompt}>
                {/* 区切り線の幅を文言 / ボタンの最大幅に合わせる (.or-divider-group) */}
                <div className="or-divider-group">
                  <p>ファイルをドラッグ&ドロップ</p>
                  <div className="or-divider"><span>または</span></div>
                  <button type="button" className="btn-modal">
                    ファイルを選択
                  </button>
                </div>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={withSvgAccept('image/*,video/*,audio/*', svgGate.acceptSvg)}
              onChange={handleFileChange}
              disabled={isBusy}
              className={styles.fileInput}
            />
          </div>

          {/* 右側：メタデータ入力 */}
          <aside className={styles.metaPane}>
            {/* モーダルタイトル */}
            <h2 className={styles.modalTitle}>素材をアップロード</h2>

            {/* フォルダ選択 */}
            <section className={styles.sidebarSection}>
              <h3>保存先</h3>
              <div className="gen-mode-switch">
                <button
                  type="button"
                  className={`gen-mode-button ${destination === 'cloud' ? 'gen-mode-button-active' : ''}`}
                  onClick={() => setDestination('cloud')}
                >
                  共有フォルダ
                </button>
                <button
                  type="button"
                  className={`gen-mode-button ${styles.personalFolderButton} ${destination === 'temporary' ? 'gen-mode-button-active' : ''}`}
                  onClick={() => setDestination('temporary')}
                >
                  個人フォルダ
                </button>
              </div>
            </section>

            {/* ファイル情報 */}
            {selectedFile && (
              <section className={styles.sidebarSection}>
                <dl>
                  <div>
                    <dt>コンテンツ種別</dt>
                    <dd>{selectedFile.type || (isVideo ? '動画' : isAudio ? '音声' : '画像')}</dd>
                  </div>
                  {!isAudio && (
                    <div>
                      <dt>サイズ</dt>
                      <dd>{dimensions ? `${dimensions.width}×${dimensions.height}` : '計算中...'}</dd>
                    </div>
                  )}
                  <div>
                    <dt>ファイル容量</dt>
                    <dd>{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</dd>
                  </div>
                </dl>
              </section>
            )}

            {/* 音声の既定用途 (声/BGM/効果音/サウンドロゴ)。音声ファイル選択時のみ表示 */}
            {isAudio && (
              <section className={styles.sidebarSection}>
                <label htmlFor="default-audio-role-select">この音声の既定用途</label>
                <select
                  id="default-audio-role-select"
                  value={defaultAudioRole}
                  onChange={(e) => setDefaultAudioRole(e.target.value as AudioReferenceUsage | '')}
                >
                  <option value="">未設定</option>
                  {DEFAULT_AUDIO_ROLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </section>
            )}

            {/* 関連商品 */}
            <section className={styles.sidebarSection}>
              <dl>
                <div>
                  <dt>関連{brandConfig.labels.productShort}</dt>
                  <dd style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <span style={{ flex: '1 1 auto', minWidth: '150px' }}>
                      <ProductLinksDisplay productLinks={selectedProducts} />
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowProductSelector(true)}
                      className="btn-modal"
                      title={`${brandConfig.labels.productShort}リストから選択`}
                    >
                      {brandConfig.labels.productShort}と関連付ける
                    </button>
                  </dd>
                </div>
              </dl>
            </section>

            {/* タグ - 区切り線付き */}
            <section className={`${styles.sidebarSection} ${styles.editSection}`}>
              <label htmlFor="tags-input">{brandConfig.labels.tag}</label>
              <TagSelector value={tags} onChange={setTags} disabled={isBusy} />
            </section>

            {/* メモ */}
            <section className={styles.sidebarSection}>
              <label htmlFor="notes-input">メモ</label>
              <textarea
                id="notes-input"
                placeholder="メモを入力"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </section>
          </aside>
        </div>

        {/* アクションボタン */}
        <footer className={styles.footer}>
          <div className={styles.footerLeft}>
            {uploadPhase === 'uploading' ? (
              <button
                type="button"
                className="btn-modal-danger"
                onClick={() => uploadAbortRef.current?.abort()}
              >
                アップロードをキャンセル
              </button>
            ) : uploadPhase === 'registering' ? (
              <span style={{ fontSize: '0.85rem', opacity: 0.8, alignSelf: 'center' }}>
                素材を登録しています...
              </span>
            ) : (
              <button
                type="button"
                className="btn-modal-danger"
                onClick={handleClose}
                disabled={isBusy}
              >
                キャンセル
              </button>
            )}
          </div>
          <div className={styles.footerRight}>
            {uploadProgress !== null && (
              <div
                aria-label="アップロード進捗"
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '0.75rem' }}
              >
                <div style={{ width: 120, height: 6, borderRadius: 3, background: 'rgba(128,128,128,0.3)', overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${Math.round(uploadProgress * 100)}%`,
                      height: '100%',
                      background: 'var(--accent-color, #4a9eff)',
                      transition: 'width 0.2s ease',
                    }}
                  />
                </div>
                <span style={{ fontSize: '0.8rem', opacity: 0.8, minWidth: '3em', textAlign: 'right' }}>
                  {Math.round(uploadProgress * 100)}%
                </span>
              </div>
            )}
            <button
              type="button"
              className="btn-modal"
              onClick={handleUpload}
              disabled={!selectedFile || isBusy}
            >
              {isBusy
                ? (selectedFile && isSvgFile(selectedFile) ? `${SVG_LAYER_LOADING_LABEL}...` : 'アップロード中...')
                : 'アップロード'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      <ProductSelectorModal
        isOpen={showProductSelector}
        onClose={() => setShowProductSelector(false)}
        onSelect={() => { /* handled by onSelectLinks */ }}
        onSelectLinks={(links) => {
          setSelectedProducts(links.map(l => ({ ...l })));
          setShowProductSelector(false);
        }}
        initialSelectedLinks={stableSelectedLinks}
      />
    </>
  );
}
